"use client";

import React, { useEffect, useState } from "react";
import DataTable, {
  ExpanderComponentProps,
  TableColumn,
} from "react-data-table-component";
import { ApiResponse, FetchParams } from "@/@types/general";
import ButtonLoader from "./ButtonLoader";
import { DATATABLE_STYLE } from "@/config/constants";

type OrderDirection = "ASC" | "DESC";

type GenericDataTableProps<
  T,
  K extends keyof TData,
  TData extends Record<K, T[]> & {
    page: number;
    total: number;
  }
> = {
  title?: string;
  depedencies?: any;
  columns: TableColumn<T>[];
  fetchData: (
    params: FetchParams & { order_by?: keyof T; order?: OrderDirection }
  ) => Promise<ApiResponse<TData>>;
  itemsKey: K;
  limit?: number;
  expandableKeys?: { value: keyof T; label: string }[];
};

const LoadingComponent = () => (
  <div className="flex flex-col w-full gap-4 relative p-8">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <ButtonLoader />
    </div>
    {Array.from({ length: 3 }).map((_, index) => (
      <div
        key={index}
        className="grid grid-cols-3 opacity-25 lg:grid-cols-7 gap-4"
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="skeleton w-full h-6"></div>
        ))}
      </div>
    ))}
  </div>
);

export default function GenericDataTable<
  T,
  K extends keyof TData,
  TData extends Record<K, T[]> & { page: number; total: number }
>({
  title,
  columns,
  fetchData,
  depedencies,
  itemsKey,
  limit = 10,
  expandableKeys,
}: GenericDataTableProps<T, K, TData>) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [orderBy, setOrderBy] = useState<keyof T>("id" as keyof T);
  const [order, setOrder] = useState<OrderDirection>("DESC");
  const [perPage, setPerPage] = useState(limit);
  const handleFetch = async () => {
    setLoading(true);
    try {
      const result = await fetchData({
        order_by: orderBy || undefined,
        order,
        limit: perPage,
        page,
        search: search || undefined,
      });
      const rawData = result.data;

      if (rawData) {
        setData(rawData[itemsKey] ?? []);
        setTotalRows(rawData.total);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, orderBy, order, perPage, depedencies]);

  const handleSearch = () => {
    setPage(1);
    handleFetch();
  };

  const ExpandedComponent = ({ data }: ExpanderComponentProps<T>) => {
    if (!expandableKeys || expandableKeys.length === 0) {
      return <pre className="text-sm">{JSON.stringify(data, null, 2)}</pre>;
    }

    return (
      <div className="p-4">
        <div className="lg:p-4 lg:border-primary lg:border-2 rounded-lg text-sm grid lg:grid-cols-2 gap-4">
          {expandableKeys.map((item) => (
            <div
              key={String(item.value)}
              className="flex max-lg:flex-col lg:items-center gap-1 lg:gap-4 text-xs"
            >
              <div className="lg:min-w-[150px] font-bold capitalize">
                {item.label}
              </div>
              <div>
                <span className="max-lg:hidden">:</span>{" "}
                {item.value ? String(data[item.value]) : "-"}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        className="flex max-lg:grid max-lg:grid-cols-2 gap-3"
      >
        {/* Search Input */}
        <input
          type="text"
          placeholder="Search..."
          className="input input-xs input-bordered w-full max-lg:col-span-2"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Sort By Dropdown */}
        <select
          className="select select-xs select-bordered"
          value={orderBy ? String(orderBy) : "id"}
          onChange={(e) => setOrderBy(e.target.value as keyof T)}
        >
          <option value="">Sort By</option>
          <option value={"id"}>ID (Default Order)</option>
          {columns.map((col, idx) => {
            const selector = col.id;
            if (typeof selector !== "string") return null;

            return (
              <option key={idx} value={selector}>
                {col.name}
              </option>
            );
          })}
          <option value={"created_at"}>Tanggal Dibuat</option>
        </select>

        {/* Sort Order Dropdown */}
        <select
          className="select select-xs select-bordered"
          value={order}
          onChange={(e) => setOrder(e.target.value as OrderDirection)}
        >
          <option value="ASC">ASC</option>
          <option value="DESC">DESC</option>
        </select>

        {/* Search Button */}
        <button className="btn btn-xs btn-primary  max-lg:col-span-2 text-white fnot-bold">
          Search
        </button>
      </form>

      {/* Table */}
      <div className="bg-base-100 rounded-box shadow">
        <DataTable
          title={title}
          dense
          columns={columns}
          data={data}
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={setPage}
          paginationPerPage={perPage}
          highlightOnHover
          progressComponent={<LoadingComponent />}
          striped
          onChangeRowsPerPage={(count) => {
            console.log({ count });
            setPerPage(count);
          }}
          customStyles={{
            ...DATATABLE_STYLE,
          }}
        />
      </div>
    </div>
  );
}
